<template>
  <Transition name="banner-fade">
    <div v-if="isVisible && promoData" class="promo-banner">
      <button class="promo-banner__close" @click="handleClose" aria-label="Закрыть баннер">
        <SvgoClose class="promo-banner__close-icon" aria-hidden="true"/>
      </button>

      <div class="promo-banner__content promo-banner__content--desktop">
        <button class="promo-banner__text" @click="handleCtaClick">
          <div class="promo-banner__title-row">
            <h4 class="promo-banner__title">{{ promoData.heading }}</h4>
            <SvgoChevronRightMd class="promo-banner__arrow-icon" aria-hidden="true"/>
          </div>
          <p class="promo-banner__subtitle">{{ promoData.subtitle }}</p>
        </button>

        <button
          type="button"
          class="promo-banner__code"
          @click="copyPromoCode"
          :aria-label="isCopied ? 'Промокод скопирован' : 'Скопировать промокод'"
        >
          <span class="promo-banner__code-label">промокод</span>
          <div class="promo-banner__code-value">
            <span>{{ promoData.promoCode }}</span>
            <SvgoCheck v-if="isCopied" class="promo-banner__icon" aria-hidden="true"/>
            <SvgoCopy v-else class="promo-banner__icon" aria-hidden="true"/>
          </div>
        </button>

        <Button
          v-if="shouldShowChooseDoctorButton"
          isButton
          classList="small promo-banner__cta-button"
          text="Выбрать врача"
          @click="handleCtaClick"
        />
      </div>

      <div class="promo-banner__content promo-banner__content--mobile">
        <button class="promo-banner__text" @click="handleCtaClick">
          <div class="promo-banner__title-row">
            <h4 class="promo-banner__title">{{ promoData.heading }}</h4>
            <SvgoChevronRightMd class="promo-banner__arrow-icon" aria-hidden="true"/>
          </div>
          <p class="promo-banner__subtitle">{{ promoData.subtitle }}</p>
        </button>

        <button
          type="button"
          class="promo-banner__code"
          @click="copyPromoCode"
          :aria-label="isCopied ? 'Промокод скопирован' : 'Скопировать промокод'"
        >
          <span class="promo-banner__code-label">промокод</span>
          <div class="promo-banner__code-value">
            <span>{{ promoData.promoCode }}</span>
            <SvgoCheck v-if="isCopied" class="promo-banner__icon" aria-hidden="true"/>
            <SvgoCopy v-else class="promo-banner__icon" aria-hidden="true"/>
          </div>
        </button>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
const route = useRoute();
const {promoData, isVisible, dismissBanner} = usePromoBanner();
const {partnerPromoPagePath, markPartnerPromoEntry, isPartnerPromoPage} = usePartnerPromoEntry();
const {isCopied, copyToClipboard, cleanup} = useClipboard();
const router = useRouter();
const MOBILE_BANNER_OFFSET = 88;
const DESKTOP_BANNER_OFFSET = 56;
const FALLBACK_DESKTOP_BANNER_BREAKPOINT = 768;
const shouldShowChooseDoctorButton = computed(() => !isPartnerPromoPage(route.path));

const getDesktopBannerBreakpoint = (): number => {
  if (!process.client) {
    return FALLBACK_DESKTOP_BANNER_BREAKPOINT;
  }

  const breakpointValue = getComputedStyle(document.documentElement)
    .getPropertyValue('--screen-sm')
    .trim();
  const parsedValue = Number.parseInt(breakpointValue, 10);

  return Number.isNaN(parsedValue) ? FALLBACK_DESKTOP_BANNER_BREAKPOINT : parsedValue;
};

const getBannerOffset = (): number => {
  return window.innerWidth > getDesktopBannerBreakpoint() ? DESKTOP_BANNER_OFFSET : MOBILE_BANNER_OFFSET;
};

const setHeaderOffset = (): void => {
  if (!process.client) return;

  if (isVisible.value && promoData.value) {
    document.documentElement.style.setProperty('--promo-banner-offset', `${getBannerOffset()}px`);
    return;
  }

  document.documentElement.style.removeProperty('--promo-banner-offset');
};

const handleCtaClick = () => {
  if (!promoData.value) return;

  markPartnerPromoEntry();
  router.push(partnerPromoPagePath);
};

const copyPromoCode = () => {
  if (!promoData.value) return;
  copyToClipboard(promoData.value.promoCode, {
    successMessage: 'Промокод скопирован в буфер обмена',
    errorMessage: 'Поддержка буфера обмена отсутствует'
  });
};

const handleClose = () => {
  dismissBanner();
};

watch([isVisible, promoData], () => {
  setHeaderOffset();
}, { immediate: true });

onMounted(() => {
  window.addEventListener('resize', setHeaderOffset);
  setHeaderOffset();
});

onUnmounted(() => {
  window.removeEventListener('resize', setHeaderOffset);
  document.documentElement.style.removeProperty('--promo-banner-offset');
  cleanup();
});
</script>

<style lang="scss" scoped>
.promo-banner {
  position: sticky;
  top: 0;
  width: 100%;
  z-index: 1000;
  background: $color-purple;
  box-shadow: 0 4px 40px 0 rgba(138, 138, 176, 0.1);
  padding: 12px 48px 12px 16px;
  overflow: hidden;

  @include media(sm, min-width) {
    display: flex;
    justify-content: center;
    padding: 8px 56px;
  }

  &__content {
    width: 100%;
    max-width: 1280px;
  }

  &__content--desktop {
    display: none;
    align-items: center;
    justify-content: center;
    gap: 40px;

    @include media(sm, min-width) {
      display: flex;
    }
  }

  &__content--mobile {
    display: flex;
    align-items: center;
    justify-content: space-evenly;
    gap: 12px;
    min-height: 64px;

    @include media(sm, min-width) {
      display: none;
    }
  }

  &__text {
    background: transparent;
    border: none;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
    margin: 0;
    padding: 0;
    text-align: left;
    min-width: 0;
  }

  &__title-row {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  &__arrow-icon {
    width: 8px;
    height: 8px;
    color: $color-on-accent;
    flex-shrink: 0;
  }

  &__title {
    font-family: Ubuntu, sans-serif;
    font-weight: 500;
    line-height: 1.1;
    letter-spacing: -0.02em;
    color: $color-on-accent;
    margin: 0;
    font-size: 18px;
  }

  &__subtitle {
    font-family: Ubuntu, sans-serif;
    font-weight: 400;
    line-height: 1.2;
    font-size: 12px;
    color: $color-on-accent;
    opacity: 0.5;
    margin: 0;
  }

  &__code {
    appearance: none;
    border: 0.5px solid $color-pink;
    border-radius: 8px;
    background: transparent;
    padding: 6px 12px;
    display: flex;
    align-items: center;
    gap: 8px;
    @include light-dark(color, #f7e7f9, $color-on-accent);
    flex-shrink: 0;
    min-height: 32px;
    cursor: pointer;
    text-align: left;
    transition: opacity 0.2s;

    &:hover {
      opacity: 0.9;
    }

    @include media(sm) {
      flex-direction: column;
      align-items: flex-start;
      justify-content: center;
      gap: 2px;
      min-height: 64px;
      padding: 4px 12px;
      width: 109px;
    }
  }

  &__code-label {
    font-family: Ubuntu, sans-serif;
    font-weight: 400;
    font-size: 16px;
    line-height: 1.35;
    color: inherit;
  }

  &__code-value {
    display: flex;
    align-items: center;
    gap: 6px;

    span {
      font-family: Ubuntu, sans-serif;
      font-weight: 500;
      font-size: 16px;
      line-height: 1.35;
      color: inherit;
    }
  }

  &__icon {
    width: 16px;
    height: 16px;
    color: inherit;
    flex-shrink: 0;
  }

  &__close {
    position: absolute;
    right: 8px;
    top: 8px;
    width: 24px;
    height: 24px;
    border: none;
    border-radius: 0;
    background: transparent;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    color: $color-on-accent;
    opacity: 1;
    transition: opacity 0.2s;

    &:hover {
      opacity: 0.8;
    }

    @include media(sm, min-width) {
      right: 12px;
      top: 50%;
      transform: translateY(-50%);
    }
  }

  &__close-icon {
    width: 14.4px;
    height: 14.4px;
    color: inherit;
  }
}

:deep(.promo-banner__cta-button.button) {
  height: 32px;
  min-height: 32px;
  padding: 0 16px;
  border: 1px solid $color-pink;
  border-radius: 50px;
  background-color: $color-pink;
  color: $color-accent-text;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.2;
  flex-shrink: 0;
}

:deep(.promo-banner__cta-button.button:hover) {
  background-color: $color-pink;
  border-color: $color-pink;
  color: $color-accent-text;
  opacity: 0.9;
}

:deep(.promo-banner__cta-button.button:active) {
  background-color: $color-pink;
  border-color: $color-pink;
  color: $color-accent-text;
}

@include media(sm, min-width) {
  .promo-banner {
    &__title {
      font-size: 18px;
      white-space: nowrap;
    }

    &__subtitle {
      white-space: nowrap;
    }

    &__title-row {
      align-items: flex-end;
      gap: 3px;
    }

    &__arrow-icon {
      margin-bottom: 4px;
    }
  }
}

.banner-fade-enter-active,
.banner-fade-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}

.banner-fade-enter-from,
.banner-fade-leave-to {
  opacity: 0;
  transform: translateY(-12px);
}
</style>
